import type { ReactNode } from "react";

export interface AppShellProps {
	readonly sidebar: ReactNode;
	readonly topbar: ReactNode;
	readonly children: ReactNode;
}

/** Fixed dark rail · glass top bar · one scrolling content column. The page itself never scrolls. */
export function AppShell({ sidebar, topbar, children }: AppShellProps) {
	return (
		<div className="page-ground flex h-dvh overflow-hidden">
			<a
				href="#main-content"
				className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-50 focus:rounded-btn focus:bg-surface focus:px-3 focus:py-2 focus:text-ink"
			>
				Skip to content
			</a>
			{sidebar}
			<div className="bench flex flex-1 flex-col overflow-hidden">
				{topbar}
				{/* data-scroll-restoration-id lets the router restore this element's scroll position on back/forward. */}
				<main
					id="main-content"
					tabIndex={-1}
					data-scroll-restoration-id="main-content"
					className="thin-scroll content-col relative z-1 flex-1 overflow-y-auto pt-4 pb-8 outline-none"
				>
					{children}
				</main>
			</div>
		</div>
	);
}

export interface PageHeaderProps {
	readonly title: string;
	readonly description?: string;
	readonly actions?: ReactNode;
}

/** 32px/600 page title, a single 13px explainer line and optional actions on the right. */
export function PageHeader({ title, description, actions }: PageHeaderProps) {
	return (
		<div className="mb-5 flex items-start">
			<div className="flex-1">
				<h1 className="mb-1.5 text-display font-semibold tracking-display text-ink">{title}</h1>
				{description === undefined ? null : (
					<p className="text-body leading-[1.55] text-ink-2">{description}</p>
				)}
			</div>
			{actions === undefined ? null : <div className="flex items-center gap-2">{actions}</div>}
		</div>
	);
}
